import { inject, Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from '../utils/auth.utils';
import { TabGetService } from '@techextensor/tab-core-utility';
import { Constants, UserRole } from '../const/constants';
import { HelperFunctionService } from './helper-function.service';
import { BroadcastChannelService } from './cross-tab-authentication.service';
import { TokenRefreshService } from './token-refresh.service';
import { OrgAppInfoHelperService } from './org-app-info-helper.service';
import { environment } from '../../../environments/environment';
import { TabSdk } from '@techextensor/tab-sdk';
import { TranslationToken } from '../const/translation.token';
import { MicrosoftSsoService } from './microsoft-sso.service';

/**
 * Must match SecurityConstants.PasswordReset.ResendCooldownMinutes on the identity service.
 * Inside this window a repeat request is silently ignored server-side and still answers
 * "code sent" (so the endpoint cannot be used to discover which addresses have accounts),
 * which means the countdown has to be tracked here or the user waits for mail that never comes.
 */
const RESEND_COOLDOWN_SECONDS = 120;

@Injectable({ providedIn: 'root' })
export class AuthService {
    public _authenticated: boolean = false;
    private _initializationComplete: boolean = false;
    public _tabGetService = inject(TabGetService);
    public _helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
    private _broadcastChannelService = inject(BroadcastChannelService);
    private _tokenRefreshService: TokenRefreshService = inject(TokenRefreshService);
    // private _sessionInactivityService: SessionInactivityService = inject(SessionInactivityService);
    private _orgAppInfoHelper: OrgAppInfoHelperService = inject(OrgAppInfoHelperService);
    private _microsoftSso: MicrosoftSsoService = inject(MicrosoftSsoService);
    // private readonly router: Router = inject(Router);

    set accessToken(token: string) {
        TabSdk.store.token = 'Bearer ' + token;
    }

    get accessToken(): string {
        return TabSdk.store.token ?? '';
    }


    constructor() {
        this._broadcastChannelService.authenticationEmitter.subscribe(isAuthenticated => {
            this._authenticated = isAuthenticated;
            if (!isAuthenticated) {
                TabSdk.signalR.disconnect();
                TabSdk.notification.disconnect();
            }
        })
    }

    // ─── Password management (TAF Identity service) ────────────────────────────
    // Routed through TabSdk.auth, which wraps core-utility's AuthService: endpoint paths
    // come from API in api-constants, the base URL from Common.apiServiceUrls.identityService,
    // the auth interceptor attaches Authorization / TenantId / AppId / EnvironmentId, and
    // responses are normalised to { IsSuccess, Result, Errors } by ResponseAdapter.

    /** Forgot password — request a 6-digit code by email (anonymous). Always succeeds (no enumeration). */
    async forgotPassword(email: string): Promise<any> {
        const response = await TabSdk.auth.forgotPassword({ email });
        this.recordResetCodeSent(email);
        return response;
    }

    // ─── Reset in progress ─────────────────────────────────────────────────────
    // One session-storage entry, { email, resendAt }, carries the reset from forgot-password to
    // reset-password:
    // - email: not in the URL, where it would land in browser history, server logs and the Referer
    //   header. The reset-password screen sends anyone without one back to step one.
    // - resendAt: the service applies a per-account resend cooldown and answers a suppressed
    //   request with the same "code sent" message, so the wait is tracked here as a countdown.
    // Session storage: the reset lives exactly as long as the tab. AppComponent clears session
    // storage on every signed-out page load, so reloading the code screen also starts over.

    private get pendingReset(): { email: string; resendAt: number } | null {
        return TabSdk.store.getSession(Constants.PasswordResetKey) || null;
    }

    /** Records that a code was just requested for this address. */
    private recordResetCodeSent(email: string): void {
        if (!email) return;
        email = email.trim();
        const current = this.pendingReset;
        // Never extend a cooldown already running for the same address: the service started
        // counting at the first code and a suppressed repeat does not restart it.
        const resendAt = current?.email === email && current.resendAt > Date.now()
            ? current.resendAt
            : Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
        TabSdk.store.setSession(Constants.PasswordResetKey, { email, resendAt });
    }

    /** The address of the reset in progress in this tab, or null when there is none. */
    pendingResetEmail(): string | null {
        return this.pendingReset?.email || null;
    }

    /** Seconds until another code may be requested; 0 when a resend is allowed. */
    resetCodeResendSecondsLeft(): number {
        const resendAt = this.pendingReset?.resendAt ?? 0;
        return Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
    }

    /** Ends the reset flow — after a successful reset, or when the user starts over. */
    clearPendingReset(): void {
        TabSdk.store.removeSession(Constants.PasswordResetKey);
    }

    /** Forgot password — redeem the emailed code and set a new password (anonymous). */
    confirmForgotPassword(payload: { email: string; code: string; newPassword: string }): Promise<any> {
        return TabSdk.auth.confirmForgotPassword(payload);
    }

    /** Self-service — change the current user's password (verifies old password; user resolved from JWT). */
    resetOwnPassword(payload: { oldPassword: string; newPassword: string; confirmNewPassword: string }): Promise<any> {
        return TabSdk.auth.resetOwnPassword(payload);
    }

    /** Admin — set another user's password by id (requires an admin role; enforced by the API). */
    adminResetPassword(userId: string, newPassword: string): Promise<any> {
        return TabSdk.auth.adminResetPassword(userId, { newPassword });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { username: string; password: string }): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return from(TabSdk.auth.login(credentials)).pipe(switchMap(async (res: any) => this.completeSignIn(res)));
    }

    /**
     * Sign in with the one-time code the Identity service put in the URL after a Microsoft sign-in.
     * The exchange returns the same body as the password login, so everything after it is shared.
     */
    signInWithMicrosoftCode(code: string): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return from(this._microsoftSso.exchangeSignInCode(code)).pipe(switchMap(async (res: any) => this.completeSignIn(res)));
    }

    /** Stores the session and routes to the right tenant/app — the same for every way of signing in. */
    private completeSignIn(res: any): any {
        const authData = this.prepareAuthData(res);
        const { subdomain, domain } = this._orgAppInfoHelper.extractDomainInfo();
        const appCode = this.extractAppCodeFromPath();

        const userInfo = res?.Result?.user;
        const defaultAppId = userInfo?.DefaultAppId?.toLowerCase();
        const defaultTenantId = userInfo?.DefaultTenantId?.toLowerCase();
        const envTenantId = environment.commonConfig.tenantId?.toLowerCase();
        const envAppId = environment.commonConfig.applicationId?.toLowerCase();
        const envAppCode = environment.commonConfig.applicationCode;

        // Case 1: Main domain without subdomain
        if (this._orgAppInfoHelper.isValidDomain(domain) && !subdomain) {
            return this.handleMainDomainLogin(authData, res, domain, defaultTenantId, defaultAppId, envTenantId, envAppId, envAppCode);
        }
        // Case 2: Subdomain without specific app
        else if (this._orgAppInfoHelper.isValidDomain(domain) && subdomain && !appCode) {
            return this.handleSubdomainLogin(authData, res, subdomain, defaultAppId, envAppId, envTenantId);
        }
        // Case 3: Specific subdomain and specific app
        else {
            return this.handleSpecificAppLogin(authData, res, domain, subdomain, appCode, envAppCode, envTenantId, envAppId);
        }
    }

    // Helper function to prepare auth data
    private prepareAuthData(res: any): any {
        return {
            userInfo: res.Result,
            token: `Bearer ${res.Result.Token}`,
            refreshToken: res?.Result?.RefreshToken,
            refreshTokenExpiry: res?.Result?.RefreshTokenExpiry,
        };
    }

    // Extract app code from path
    private extractAppCodeFromPath(): string | null {
        const match = document?.location?.pathname?.match(/^\/([^\/]+)/);
        return match?.[1] && match?.[1] !== 'sign-in' && match?.[1] !== 'sign-up' ? match[1] : null;
    }

    // Check if user has tenant admin role
    private hasTenantAdminRole(roles: any, envAppId: any): boolean {
        let isTenantAdmin = false;

        Object.keys(roles).forEach((key: any) => {
            const defaultAppRoles = roles[key]?.[envAppId]?.[environment?.id];
            defaultAppRoles?.forEach((role: any) => {
                if (role?.RoleName === UserRole.TenantAdministratorRoleName) {
                    isTenantAdmin = true;
                }
            });
        })
        return isTenantAdmin;
    }

    // Set window data and redirect
    private setWindowDataAndRedirect(
        authData: any, appCode: string, url: string, useRouter: boolean = false,
        queryParams: any = {}, preserveQueryParams: boolean = true,
        orgInfo?: any, appInfo?: any
    ): any {
        if (useRouter) {
            url = this.buildUrlWithQueryParams(url, queryParams, preserveQueryParams);
        }

        window.location.replace(this._helperFunctionService.buildAuthTransferUrl(url, {
            authData,
            appCode: appCode || environment.commonConfig.applicationCode,
            ...(orgInfo?.Id && { orgInfo }),
            ...(appInfo?.Id && { appInfo }),
        }));
        return of(null);
    }

    // Helper method to build URL with query parameters
    private buildUrlWithQueryParams(baseUrl: string, newQueryParams: any = {}, preserveQueryParams: boolean = true): string {
        const currentUrl = new URL(window.location.href);
        const url = new URL(baseUrl, window.location.origin);

        // If preserveQueryParams is true, start with existing query parameters
        if (preserveQueryParams) {
            currentUrl.searchParams.forEach((value, key) => {
                url.searchParams.set(key, value);
            });
        }

        // Add/override with new query parameters
        Object.keys(newQueryParams).forEach(key => {
            if (newQueryParams[key] !== undefined && newQueryParams[key] !== null) {
                url.searchParams.set(key, newQueryParams[key]);
            }
        });

        return url.toString();
    }

    // Handle unauthorized access
    private handleUnauthorizedAccess(appCode: string): any {
        TabSdk.ui.showError({
            title: TranslationToken.AuthorizationFailed,
            message: TranslationToken.UnauthorizedToAccessApp,
        });
        return of(null);
    }

    // Main domain login handler
    private async handleMainDomainLogin(
        authData: any, res: any, domain: any, defaultTenantId: string,
        defaultAppId: string, envTenantId: string, envAppId: string, envAppCode: string
    ): Promise<any> {
        let url = 'https://';
        let defaultAppCode;

        let orgInfo: any;
        let appInfo: any;

        // Default tenant matches environment tenant
        if (defaultTenantId === envTenantId) {
            defaultAppCode = envAppCode;
            url += `${environment.commonConfig.tenantName}.${domain}/initializing`;
            orgInfo = TabSdk.store.getOrgInfo();
        }
        // Default app matches environment app
        else if (defaultAppId === envAppId) {
            if (defaultTenantId !== envTenantId) {
                if (!this.hasTenantAdminRole(res?.Result?.user?.Roles, envAppId)) {
                    return this.handleUnauthorizedAccess(envAppCode);
                } else {
                    const orgInfoRes: any = await firstValueFrom(this._orgAppInfoHelper.getOrgInfoApiObservable({ Id: defaultTenantId }));
                    orgInfo = orgInfoRes?.Result?.[0];
                    const defaultSubdomain = orgInfo?.Subdomain;
                    defaultAppCode = envAppCode;
                    url += `${defaultSubdomain}.${domain}/initializing?redirectURL=admin`;
                }
            } else {
                defaultAppCode = envAppCode;
                url += `${environment.commonConfig.tenantName}.${domain}/initializing`;
                orgInfo = TabSdk.store.getOrgInfo();
            }
        }
        // Neither matches environment defaults — fetch both
        else {
            const orgInfoRes: any = await firstValueFrom(this._orgAppInfoHelper.getOrgInfoApiObservable({ Id: defaultTenantId }));
            const appDetailResponse: any = await firstValueFrom(this._orgAppInfoHelper.getAppInfoApiObservable({ AppId: defaultAppId }));
            orgInfo = orgInfoRes?.Result?.[0];
            appInfo = appDetailResponse?.Result?.[0];
            defaultAppCode = appInfo?.AppName;
            url += `${orgInfo?.Subdomain}.${domain}/initializing`;
        }

        return this.setWindowDataAndRedirect(authData, defaultAppCode, url, false, {}, true, orgInfo, appInfo);
    }

    // Subdomain login handler
    private async handleSubdomainLogin(
        authData: any, res: any, subdomain: any, defaultAppId: string,
        envAppId: string, envTenantId: string
    ): Promise<any> {
        let url = 'initializing';
        let defaultAppCode;
        let queryParams = {};
        let preserveQueryParams = true;

        const orgInfo = TabSdk.store.getOrgInfo();
        let appInfo: any;

        // Default app matches environment app
        if (defaultAppId === envAppId) {
            if (subdomain !== environment.commonConfig.tenantName) {
                if (!this.hasTenantAdminRole(res?.Result?.user?.Roles, envAppId)) {
                    return this.handleUnauthorizedAccess(environment.commonConfig.applicationCode);
                } else {
                    defaultAppCode = environment.commonConfig.applicationCode;
                    queryParams = { redirectURL: 'admin' };
                    preserveQueryParams = false;
                }
            } else {
                defaultAppCode = environment.commonConfig.applicationCode;
            }
        }
        // Default app doesn't match environment app — fetch it
        else {
            const appDetailResponse: any = await firstValueFrom(this._orgAppInfoHelper.getAppInfoApiObservable({ AppId: defaultAppId }));
            const defaultAppDetail = appDetailResponse?.Result?.[0];
            this._orgAppInfoHelper.setAppInfo(defaultAppDetail);
            appInfo = defaultAppDetail;
            defaultAppCode = defaultAppDetail.AppName;
        }

        return this.setWindowDataAndRedirect(authData, defaultAppCode, url, true, queryParams, preserveQueryParams, orgInfo, appInfo);
    }

    // Specific app login handler
    private handleSpecificAppLogin(
        authData: any, res: any, domain: any, subdomain: any, appCode: any,
        envAppCode: string, envTenantId: string, envAppId: string
    ): Promise<any> {
        let url = 'initializing';
        let queryParams = {};
        let preserveQueryParams = true;

        // App code matches environment app code
        if (appCode === envAppCode && this._orgAppInfoHelper.isValidDomain(domain)) {
            if (subdomain !== environment.commonConfig.tenantName) {
                if (!this.hasTenantAdminRole(res?.Result?.user?.Roles, envAppId)) {
                    return this.handleUnauthorizedAccess(envAppCode);
                } else {
                    queryParams = { redirectURL: 'admin' };
                    preserveQueryParams = false;
                }
            }
        }

        return this.setWindowDataAndRedirect(authData, appCode, url, true, queryParams, preserveQueryParams, TabSdk.store.getOrgInfo());
    }

    /**
     * Sign up a new tenant user
     *
     * @param credentials - The user's credentials including first name, last name, email, password, tenant name, and terms
     * @returns An Observable that emits the response from the server
     */
    signUp(credentials: {
        AdminFirstName: string, AdminLastName: string, AdminEmail: string,
        Password: string, TenantName: string, Domain: string
    }): Observable<any> {
        const payload = {
            organizationName: credentials.TenantName,
            subdomain: credentials.Domain,
            adminFirstName: credentials.AdminFirstName,
            adminLastName: credentials.AdminLastName,
            adminEmail: credentials.AdminEmail,
            password: credentials.Password,
        };

        return from(TabSdk.auth.registerTenant(payload)).pipe(
            switchMap(async (res: any) => {

                TabSdk.store.tenantId = res?.Result?.user?.DefaultTenantId;
                TabSdk.store.refreshToken = res?.Result?.RefreshToken;
                TabSdk.store.setUserInfo(res?.Result);

                this.accessToken = res?.Result?.Token;
                this._authenticated = true;

                return of(res);
            })
        );
    }

    signInUsingToken(): Observable<any> {
        if (AuthUtils._verifyJWTToken(this.accessToken)) {
            this._authenticated = true;
            return of(true);
        }
        else {
            return of(false);
        }
    }

    /**
     * Sign out the user by removing the user info from local storage and the access token from session storage.
     * Also, clear the IndexedDB.
     *
     * @return {Observable<any>} A observable that emits true when the user is successfully signed out.
     */
    signOut(backtrace: boolean = true): Observable<any> {

        this._broadcastChannelService.broadcastSignOut(backtrace);
        this._tokenRefreshService.resetServiceState();
        TabSdk.signalR.disconnect();
        TabSdk.notification.disconnect();

        // Return an observable that emits true
        return of(true);
    }

    check(): Observable<boolean> {
        if (!this._initializationComplete) {
            return new Observable((observer) => {
                this._broadcastChannelService.initialize().then((result: any) => {
                    this._initializationComplete = true;
                    this._authenticated = result;
                    observer.next(result);
                })
            });
        }

        if (this._authenticated) {
            TabSdk.store.removeStaticToken();
            return of(true);
        }


        if (!this.accessToken) {
            TabSdk.store.staticToken = environment.commonConfig.staticToken;
            return of(false);
        }

        if(!this._authenticated){
            TabSdk.store.removeToken();
            TabSdk.store.staticToken = environment.commonConfig.staticToken;
            return of(false);
        }

        return this.signInUsingToken();
    }
}
